import dayjs from "dayjs";
import { useAuthContext } from "../../user/AuthProvider";
import { PLAN_RECURRING_TYPE } from "../../../actions/constants";
import { useState, useMemo, useCallback, useEffect } from "react";
import { getFormattedDayjsDate, roundCredits } from "../../../utills/common";
import { App, Tag, Spin, Alert, Button, Card, Divider, Flex, Modal, Tooltip, Typography } from "antd";
import CustomIcon from "../../layouts/CustomIcon";
import SeatChange from "../../subscriptions/layouts/SeatChange";

const { Title, Text, Paragraph } = Typography;

export default function ManageSeatsModal({ open = false }) {
  const { notification } = App.useApp();
  const { workspace, planInfo, organization, updateState, userActions, updateWorkspace } = useAuthContext();
  const { max = 0, min = 0, occupied = 0, allowed = 0, available = 0 } = organization?.seats || {};

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [numberOfSeats, setNumberOfSeats] = useState(0);
  const [showManageBtn, setShowManageBtn] = useState(false);
  const [isUpdatingSeats, setIsUpdatingSeats] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    name = "",
    currentSeats,
    updatedSeats,
    priceFull = 0,
    quantityTotal = 0,
    quantityMonthly = 0,
    startDate = dayjs(),
    subscriptionType = PLAN_RECURRING_TYPE.MONTH,
  } = quote?.plan || {};
  const isSeatsChanged = numberOfSeats !== allowed;
  const currentPlanSeats = quantityTotal - quantityMonthly;
  const isMonthlyPlan = subscriptionType === PLAN_RECURRING_TYPE.MONTH;
  const minRequiredSeats = Math.max(0, occupied, isMonthlyPlan ? min : currentPlanSeats);

  // Prorated seats calculation
  const proratedSeatsTooltipMsg = useMemo(() => {
    const { addOn, monthlyAddOn } = updatedSeats || {};

    // Yearly plan
    if (!isMonthlyPlan && monthlyAddOn?.quantity > 0) {
      // const proratedSeats = Math.max(0, monthlyAddOn?.quantity, currentSeats?.monthlyAddOn?.quantity);
      // return `Prorated charges for ${proratedSeats} additional Full Seat${
      //   proratedSeats > 1 ? "s" : ""
      // } (based on days remaining or used seats in current month cycle)`;
      return `Prorated charges for additional monthly seats (based on days remaining or used seats in current month cycle)`;
    }

    // Monthly plan
    if (isMonthlyPlan && addOn?.quantity > 0) {
      return `Prorated charges for additional full seats (based on days remaining or used seats in current month cycle)`;
    }

    return "Prorated charges for removed seats (used in current month cycle)";
  }, [isMonthlyPlan, updatedSeats]);

  // New seats to be added
  const newSeats = useMemo(() => numberOfSeats - allowed, [numberOfSeats, allowed]);

  // Prorated charges
  const proratedCharges = useMemo(() => {
    const stripeMeter = Number(planInfo?.balances?.credits?.used?.stripeMeter) || 0;
    return updatedSeats?.total?.proratedCharge - (stripeMeter > 0 ? stripeMeter : 0);
  }, [updatedSeats?.total?.proratedCharge, planInfo?.balances?.credits?.used?.stripeMeter]);

  // Alert message for seat limits
  const alertMessage = useMemo(() => {
    // Check if the user is trying to descrease seats beyond the allowed limit
    if (showWarning && numberOfSeats <= min) {
      return `${name} Plan requires minimum ${min} Full Seat${min > 1 ? "s" : ""} in your plan.`;
    }

    if (showWarning && numberOfSeats >= max) {
      return `You have reached the maximum limit of additional seats for this plan. Please upgrade your plan to add more seats.`;
    }

    // Check if the user is trying to decrease seats below the required minimum in yearly plan
    if (!isMonthlyPlan && showWarning && numberOfSeats <= currentPlanSeats) {
      return `You can't remove seats that you have already paid for in the current annual plan.`;
    }

    // Check if the user is trying to remove seats that are in use
    if (showWarning && numberOfSeats <= occupied) {
      const members = occupied - numberOfSeats + 1;
      return `${members} member${
        members > 1 ? "s" : ""
      } has an editor role billed monthly. Please change their role before removing the seat.`;
    }

    return "";
  }, [name, numberOfSeats, min, max, occupied, showWarning, isMonthlyPlan, currentPlanSeats]);

  // Reset Local State
  const resetState = useCallback(() => {
    setQuote(null);
    setLoading(false);
    setShowWarning(false);
    setShowManageBtn(false);
    setNumberOfSeats(allowed);
    setIsUpdatingSeats(false);
    setShowConfirmation(false);
  }, [allowed]);

  // Reset state
  useEffect(() => {
    if (open) resetState();
  }, [open, resetState]);

  // Fetch quote for the new plan
  useEffect(() => {
    if (open && numberOfSeats > 0) {
      setLoading(true);
      userActions
        .quoteOrganizationSubscriptionSeats(organization?.id, { numberOfSeats })
        .then((response) => setQuote(response?.data?.quote))
        .catch((error) => console.error("Error fetching quote:", error))
        .finally(() => setLoading(false));
    }
  }, [open, numberOfSeats, organization?.id, userActions]);

  // On Modal Close
  const onCancel = useCallback(() => updateState({ openManageSeatsModal: false }), [updateState]);

  // On Seats Change
  const onSeatsChange = useCallback(
    (value) => {
      // Check if the new number of seats is less than the minimum required
      if (value < min || value < minRequiredSeats || value > max) {
        setShowWarning(true);
        setShowManageBtn(value > max || value < occupied);
        return;
      }

      setShowWarning(false);
      setNumberOfSeats(value);
    },
    [min, max, minRequiredSeats, occupied]
  );

  // On Update Seats
  const onUpdateSeats = useCallback(() => {
    setIsUpdatingSeats(true);
    userActions
      .updateOrganizationSubscriptionSeats(organization?.id, { numberOfSeats })
      .then(() => {
        // Update workspace and organization people
        updateWorkspace(workspace?.id, null, { fetchReqDetails: true });

        notification.success({
          title: "Success",
          description: `Seats updated successfully to ${numberOfSeats} Full Seat${numberOfSeats > 1 ? "s" : ""}.`,
        });
        onCancel();
      })
      .catch((error) => notification.error({ title: "Error", description: error || "Error updating seats" }))
      .finally(() => setIsUpdatingSeats(false));
  }, [numberOfSeats, onCancel, workspace?.id, organization?.id, notification, userActions, updateWorkspace]);

  return (
    <Modal
      centered
      open={open}
      onCancel={onCancel}
      width={showConfirmation ? 500 : 968}
      afterOpenChange={(open) => !open && resetState()}
      title={`${showConfirmation ? "Confirm" : "Manage"} Seats`}
      cancelButtonProps={{ disabled: loading, className: showConfirmation ? "w-50" : "" }}
      okText={showConfirmation ? `Yes, Add ${newSeats} seat${newSeats > 1 ? "s" : ""}` : "Confirm"}
      classNames={{ footer: showConfirmation ? "flex-item" : "", container: showConfirmation ? "br-12px" : "minh-450px" }}
      okButtonProps={{
        loading: isUpdatingSeats,
        className: showConfirmation ? "w-50" : "",
        disabled: !!alertMessage || !isSeatsChanged || loading,
      }}
      onOk={() => {
        if (newSeats > 0 && available > 0 && !showConfirmation) setShowConfirmation(true);
        else onUpdateSeats();
      }}
    >
      {showConfirmation ? (
        <Paragraph className='mb-4'>
          You have {available} unassigned full seat(s) in your plan. Are you sure you want to purchase additional seat(s)?
        </Paragraph>
      ) : (
        <Spin spinning={quote === null}>
          <Card className='mb-4 mt-3' styles={{ body: { padding: "16px 24px" } }}>
            <Flex gap={16} justify='space-between'>
              <Flex vertical>
                <Title level={5} className='m-0 fw-600'>
                  Total Seats
                </Title>
                <Paragraph type='secondary' className='m-0'>
                  {quantityTotal} Full Seat{Number(quantityTotal) > 1 ? "s" : ""} in your current subscription
                </Paragraph>
              </Flex>

              <div className='width-120px'>
                <SeatChange
                  max={max}
                  min={min}
                  allowMinDecrease
                  value={numberOfSeats}
                  onChange={onSeatsChange}
                  showWarning={showWarning}
                  disabled={quote === null}
                />
              </div>
            </Flex>

            {alertMessage && (
              <Alert
                banner
                showIcon
                type='warning'
                title={alertMessage}
                closable={!showManageBtn ? { onClose: () => setShowWarning(false) } : false}
                className='br-8px color-8A6100 mt-2'
                icon={<CustomIcon name='yellow_warning_triangle' height={24} width={24} />}
                action={
                  showManageBtn && (
                    <Button type='text' size='small' className='color-8A6100 text-underline-offset' onClick={onCancel}>
                      Manage
                    </Button>
                  )
                }
              />
            )}
          </Card>

          <Flex gap={40} justify='space-between' className='mb-4'>
            <PriceItem
              isCurrent
              name={name}
              priceFull={priceFull}
              startDate={startDate}
              seatsObj={currentSeats}
              isMonthlyPlan={isMonthlyPlan}
            />

            <CustomIcon name='line_arrow_right' width={30} height={8} className='color-6C6C89' />

            <div className='flex-1'>
              <Spin spinning={loading && quote !== null}>
                <PriceItem
                  name={name}
                  isCurrent={false}
                  priceFull={priceFull}
                  startDate={startDate}
                  seatsObj={updatedSeats}
                  isMonthlyPlan={isMonthlyPlan}
                />

                {proratedCharges > 0 && (
                  <div className='flex-item space-between p-8-16px'>
                    <Text className='fw-600 flex-item'>
                      Prorated charges
                      <Tooltip title={proratedSeatsTooltipMsg}>
                        <Button
                          type='text'
                          size='small'
                          shape='circle'
                          className='color-A9A9BC'
                          icon={<CustomIcon name='info_circle_filled' />}
                        />
                      </Tooltip>
                    </Text>
                    <Text className='fw-700'>${roundCredits(proratedCharges || 0, 2)}</Text>
                  </div>
                )}
              </Spin>
            </div>
          </Flex>
        </Spin>
      )}
    </Modal>
  );
}

function PriceItem({ name = "", priceFull = 0, startDate = dayjs(), isMonthlyPlan = false, seatsObj = null, isCurrent = false }) {
  const [collapsed, setCollapsed] = useState(false);
  const recurringText = isMonthlyPlan ? "Monthly" : "Annually";

  return (
    <div className={`flex-1 p-16px ${!isCurrent ? "bg-F4F9FF br-8px" : ""}`}>
      <Flex gap={16} align='center' justify='space-between'>
        <Title level={5} className='m-0 fw-600'>
          Seats in {isCurrent ? "Current" : "New"} Plan
        </Title>
        <Text type='secondary'>{getFormattedDayjsDate(isCurrent ? startDate : dayjs(startDate).add(1, "month"))}</Text>
      </Flex>

      <Divider className='mt-1 mb-1' />

      <Flex gap={10} justify='space-between'>
        <Paragraph className='fw-600'>
          {name} Plan (Billed {recurringText})
        </Paragraph>

        {!isMonthlyPlan && (
          <Flex align='center'>
            <Tag className='bg-F7F7F8 color-3F3F50 br-8px fw-400 border-0 font-size-12px'>Paid</Tag>
            <Button
              type='text'
              size='small'
              shape='circle'
              onClick={() => setCollapsed((c) => !c)}
              icon={<CustomIcon name={collapsed ? "chev_down" : "chev_up"} height={24} width={24} />}
            />
          </Flex>
        )}
      </Flex>

      <div className={collapsed ? "d-none" : ""}>
        <Paragraph className='flex-item space-between'>
          <Text className='fw-500'>
            {seatsObj?.included?.quantity} Full Seat{seatsObj?.included?.quantity > 1 ? "s" : ""}
          </Text>
          <Text className={`fw-700 ${!isMonthlyPlan ? "color-A9A9BC" : ""}`}>${priceFull}</Text>
        </Paragraph>

        {seatsObj?.addOn?.quantity > 0 && (
          <Flex gap={16} justify='space-between'>
            <Flex vertical>
              <Text className='fw-500'>
                {seatsObj?.addOn?.quantity} Additional Seat{seatsObj?.addOn?.quantity > 1 ? "s" : ""}
              </Text>

              {isMonthlyPlan && (
                <Text className='font-size-12px' type='secondary'>
                  ${seatsObj?.addOn?.priceFull / seatsObj?.addOn?.quantity || 0}/month per full Seat
                </Text>
              )}
            </Flex>
            <Text className={`fw-700 ${!isMonthlyPlan ? "color-A9A9BC" : ""}`}>${seatsObj?.addOn?.priceFull}</Text>
          </Flex>
        )}
      </div>

      <Divider className='mt-1 mb-1' />

      {seatsObj?.monthlyAddOn?.quantity > 0 && (
        <>
          <Paragraph className='fw-600'>{isCurrent ? "Add-ons" : "Additional Seats"} (Billed Monthly)</Paragraph>
          <Flex gap={16} justify='space-between'>
            <Flex vertical>
              <Text className='fw-500'>
                {seatsObj?.monthlyAddOn?.quantity} Additional Seat{seatsObj?.monthlyAddOn?.quantity > 1 ? "s" : ""}
              </Text>
              <Text className='font-size-12px' type='secondary'>
                ${seatsObj?.monthlyAddOn?.priceFull / seatsObj?.monthlyAddOn?.quantity || 0}/month per full Seat
              </Text>
            </Flex>
            <Text className='fw-700'>${seatsObj?.monthlyAddOn?.priceFull}</Text>
          </Flex>
          <Divider className='mt-1 mb-1' />
        </>
      )}

      <Flex gap={16} justify='space-between'>
        <Text className='fw-600'>{!isCurrent ? "Projected" : ""} Monthly Bill</Text>
        <Text className='fw-700'>${isMonthlyPlan ? seatsObj?.total?.subtotal : seatsObj?.monthlyAddOn?.priceFull}</Text>
      </Flex>
    </div>
  );
}
